/**
 * Whisper predicts the spoken language as the first token after the start
 * token. transformers.js doesn't do this step and silently assumes English,
 * which makes Whisper *translate* other languages into English, so we do it.
 */

/** Language tokens look like `<|tr|>`; returns the code inside, or null. */
const languageCode = (token: string): string | null => /^<\|([a-z]{2,3}(?:-[a-z]+)?|haw|yue)\|>$/i.exec(token)?.[1]?.toLowerCase() ?? null;

/**
 * The language whose token scores highest in `logits` (scores for the first
 * generated token). Null when the model has no language tokens.
 */
export function pickLanguage(logits: ArrayLike<number>, languageTokens: Readonly<Record<string, number>>): string | null {
  let best: string | null = null;
  let bestScore = -Infinity;
  for (const [token, id] of Object.entries(languageTokens)) {
    const code = languageCode(token);
    const score = logits[id];
    if (code && score !== undefined && score > bestScore) {
      best = code;
      bestScore = score;
    }
  }
  return best;
}

/**
 * Start of the `length`-sample window with the most energy, searched in `step`
 * increments: language detection needs speech, not the quiet intro.
 */
export function loudestWindow(samples: Float32Array, length: number, step: number): number {
  if (samples.length <= length) return 0;
  // Prefix sums of squares make every window O(1).
  const prefix = new Float64Array(samples.length + 1);
  for (let i = 0; i < samples.length; i++) prefix[i + 1] = prefix[i]! + samples[i]! * samples[i]!;
  let bestStart = 0;
  let bestEnergy = -1;
  for (let start = 0; start + length <= samples.length; start += Math.max(1, step)) {
    const energy = prefix[start + length]! - prefix[start]!;
    if (energy > bestEnergy) {
      bestEnergy = energy;
      bestStart = start;
    }
  }
  return bestStart;
}

/**
 * What to assume when the speech itself gives no answer: the person's own
 * language (from the browser), which beats Whisper's built-in English.
 */
export function fallbackSpeechLanguage(browserLanguages: readonly string[]): string {
  for (const language of browserLanguages) {
    const base = language.toLowerCase().split('-')[0];
    if (base && /^[a-z]{2,3}$/.test(base)) return base;
  }
  return 'en';
}
